import React from 'react';
import {
  Configure,
  Index,
  InstantSearch,
  useHits,
  useInstantSearch,
  useSearchBox,
} from 'react-instantsearch';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import ChatIcon from '@mui/icons-material/ChatBubbleOutline';
import ChatOutlined from '@mui/icons-material/ChatOutlined';
import { format } from 'date-fns';
import { capitalize, useTheme } from '@mui/material';
import { AppointmentStatus } from '@prisma/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { AddRounded } from '@mui/icons-material';
import SearchModal, { Props as SearchModalProps } from '../global/SearchModal';
import { INDEX, searchClient } from '../../algoliaconfig';
import useUser from '../../hooks/global/useUser';
import useRestrictedBusinessIds from '../../hooks/global/useRestrictedBusinessIds';
import { LoadedUser } from '../../interfaces';
import { applyAlgoliaRestriction, getFullName } from '../../helpers/global';
import { buildCustomerProfileUrl } from '../../helpers/customers';
import { getFullTattooLocation } from '../../utils/global/helper';
import { ROUTES } from '../../constants/global';
import useSearchShortcut from '../../hooks/ui/useSearchShortcut';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import useSearchModal from '../../hooks/global/useSearchModal';
import { AppointmentHit } from '../../types/appointments';
import useSearchChatsByCustomers from '../../hooks/chat/useSearchChats';
import useSearchGuests from '../../hooks/customers/useSearchGuests';
import useUpdateChatParticipants from '../../hooks/chat/useUpdateChatParticipants';
import useLoadingModal from '../../hooks/global/useLoadingModal';
import CustomerForm from '../form/CustomerForm';
import FullPageModal from '../modal/FullPageModal';

const getAppointmentStatusText = (status: AppointmentStatus | 'ALL') =>
  `${capitalize(status.toLowerCase())} appointments`;

function Wrapper({ children }: React.PropsWithChildren) {
  const { user } = useUser<LoadedUser>();
  const restrictedBusinessIds = useRestrictedBusinessIds();
  const { appointmentStatus, type } = useSearchModal();

  const algoliaFilters = React.useMemo(() => {
    const noTemporaryFilter = ' AND NOT status:TEMPORARY';
    const statusString =
      appointmentStatus && appointmentStatus !== 'ALL'
        ? ` AND status:${appointmentStatus}`
        : '';
    const shopFilterString = `businessId:${user?.business.id}${statusString}`;
    const artistFilterString = `employeeId:${user?.userId}${statusString}`;
    const restrict = (filter: string) =>
      applyAlgoliaRestriction(filter, restrictedBusinessIds);

    if (user?.role === 'EMPLOYEE') {
      const canSearchShopWide =
        user?.business?.type === 'INDEPENDENT' ||
        (type === 'customer-chat' && user?.canViewAllShopCustomers);
      if (canSearchShopWide) {
        return `${restrict(
          `(${shopFilterString}) OR (${artistFilterString})`,
        )}${noTemporaryFilter}`;
      }
      return `${restrict(artistFilterString)}${noTemporaryFilter}`;
    }

    return `${restrict(shopFilterString)}${noTemporaryFilter}`;
  }, [
    appointmentStatus,
    type,
    user?.business.id,
    user?.business?.type,
    user?.canViewAllShopCustomers,
    user?.role,
    user?.userId,
    restrictedBusinessIds,
  ]);

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={INDEX}
      insights
      future={{
        preserveSharedStateOnUnmount: true,
      }}>
      <Index indexName={INDEX}>
        <Configure
          filters={algoliaFilters}
          distinct={false}
          // maximum hits per page is 1000
          hitsPerPage={1000}
          // TODO: Fix customRanking
          // customRanking={
          //   sort
          //     ? JSON.stringify([
          //         `${sort.direction}(${sort.by})`,
          //         'desc(recordUpdatedAt)',
          //       ])
          //     : undefined
          // }
        />
        {children}
      </Index>
    </InstantSearch>
  );
}

function CustomerAppointmentSearchComponent({
  setIsCustomerFormOpen,
}: {
  setIsCustomerFormOpen: (isOpen: boolean) => void;
}) {
  const {
    isOpen,
    toggle: toggleOpen,
    appointmentsOnly,
    placeholder,
    itemButton,
    appointmentStatus,
    update,
    type,
  } = useSearchModal();
  const searchBox = useSearchBox();
  const { status } = useInstantSearch();
  const { results } = useHits();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();
  const { mutateAsync: updateChatParticipants } = useUpdateChatParticipants();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const isLoading = useDebouncedValue(status === 'loading', 100);

  const distinctCustomers = React.useMemo(() => {
    const customerIds = new Set<string>();
    return (
      results?.hits?.filter(hit => {
        if (customerIds.has(hit.customerId)) {
          return false;
        }
        customerIds.add(hit.customerId);
        return true;
      }) ?? []
    );
  }, [results?.hits]);

  const isChatSearchEnabled = type === 'chat' || type === 'customer-chat';
  const { data: chats } = useSearchChatsByCustomers(
    distinctCustomers.map(c => c.customerId),
    {
      enabled: isChatSearchEnabled,
    },
  );

  const isGuestSearchEnabled =
    !itemButton &&
    !appointmentsOnly &&
    type !== 'chat' &&
    type !== 'customer-chat';
  const { data: guests } = useSearchGuests(searchBox.query, {
    enabled: isGuestSearchEnabled,
  });

  const appointmentsData = React.useMemo<SearchModalProps['data'][number]>(
    () => ({
      title: 'Appointments',
      results: (
        (results?.hits ?? [])?.map(hit => {
          if (!hit.status) return null;
          const isRequest = ['REQUESTED', 'ACCEPTED', 'REJECTED'].includes(
            hit.status,
          );
          const isComplete = !!hit.checkoutSessionCompleted;
          const fullLocation = getFullTattooLocation(
            hit.tattooLocation,
            hit.bodySide,
          );

          return {
            type: 'appointment',
            id: hit.objectID,
            title: getFullName({
              firstName: hit.firstName,
              lastName: hit.lastName,
            }),
            icon: isRequest
              ? NotificationsOutlinedIcon
              : CalendarTodayOutlinedIcon,
            details: [
              `Appointment ${
                isComplete ? 'completed' : hit.status?.toLowerCase()
              }`,
              hit.startDateTime
                ? format(new Date(hit.startDateTime), 'EEEE, MMMM do yyyy')
                : `Updated on ${format(
                    new Date(hit.updatedAt),
                    'EEEE, MMMM do yyyy',
                  )}`,
              hit?.serviceCategory
                ? `${fullLocation ? `${fullLocation} ` : ''}${capitalize(
                    hit.serviceCategory?.toLowerCase() || '',
                  )}`
                : '',
            ].filter(Boolean),
            href: itemButton
              ? undefined
              : isRequest
              ? `${ROUTES.requests}/${hit.objectID}`
              : `${ROUTES.calendar}/${hit.objectID}`,
            itemButton: itemButton
              ? {
                  content: itemButton?.content,
                  onClick: () =>
                    itemButton?.onClick({
                      type: 'appointment',
                      item: hit as unknown as AppointmentHit,
                    }),
                }
              : undefined,
          };
        }) ?? []
      ).filter(Boolean) as SearchModalProps['data'][number]['results'],
    }),
    [itemButton, results?.hits],
  );

  const customerData = React.useMemo<SearchModalProps['data'][number]>(
    () => ({
      title: 'Customers',
      results:
        distinctCustomers?.map(hit => ({
          type: 'customer',
          id: hit.objectID,
          title: getFullName({
            firstName: hit.firstName,
            lastName: hit.lastName,
          }),
          icon: PeopleOutlineIcon,
          href: itemButton
            ? undefined
            : buildCustomerProfileUrl(hit.customerId, location),
          itemButton: itemButton
            ? {
                content: itemButton?.content,
                onClick: () =>
                  itemButton?.onClick({
                    type: 'customer',
                    item: hit as unknown as AppointmentHit,
                  }),
              }
            : undefined,
        })) ?? [],
      addAction: {
        icon: AddRounded,
        label: 'Add New Customer',
        onClick: () => {
          setIsCustomerFormOpen(true);
        },
      },
    }),
    [distinctCustomers, itemButton, location, setIsCustomerFormOpen],
  );

  const guestsData = React.useMemo<SearchModalProps['data'][number]>(
    () => ({
      title: 'Guests',
      results: (guests ?? []).map(guest => {
        const accountName = getFullName({
          firstName: guest.customer.user.firstName,
          lastName: guest.customer.user.lastName,
        });
        return {
          type: 'guest',
          id: guest.id,
          icon: PersonOutlineIcon,
          title: getFullName({
            firstName: guest.firstName,
            lastName: guest.lastName,
          }),
          details: accountName ? [`Under ${accountName}`] : undefined,
          href: `${ROUTES.customers}/${guest.customerId}`,
        };
      }),
    }),
    [guests],
  );

  const chatsData = React.useMemo<SearchModalProps['data'][number]>(
    () => ({
      title: 'Chats',
      results: (chats ?? []).map(chat => ({
        type: 'chat',
        id: chat.id,
        icon: ChatIcon,
        title: getFullName({
          firstName: chat.customer.user.firstName,
          lastName: chat.customer.user.lastName,
        }),
        details: [
          `Created on ${format(
            new Date(chat.createdAt),
            'EEEE, MMMM do yyyy',
          )}`,
          chat?.businessId && !chat?.employeeId
            ? 'Shop Conversation'
            : undefined,
        ].filter(Boolean) as string[],
        itemButton: {
          content: <ChatOutlined htmlColor={theme.palette.darkGrey.main} />,
          onClick: async () => {
            try {
              openLoadingModal({
                header: 'Loading chat',
                message: 'Please wait while we load the chat',
              });
              await updateChatParticipants(chat.externalId);
              navigate(`${ROUTES.chat}/${chat.externalId}`);
            } catch {
              // Empty
            } finally {
              closeLoadingModal();
            }
          },
        },
      })),
    }),
    [
      chats,
      closeLoadingModal,
      navigate,
      openLoadingModal,
      theme.palette.darkGrey.main,
      updateChatParticipants,
    ],
  );

  const data = React.useMemo(() => {
    const tempData = [];
    if (type !== 'customer-chat') {
      tempData.push(appointmentsData);
    }
    if (!appointmentsOnly || type === 'customer-chat') {
      tempData.push(customerData);
    }
    if (isGuestSearchEnabled && guests?.length) {
      tempData.push(guestsData);
    }
    if (isChatSearchEnabled && chats?.length) {
      tempData.unshift(chatsData);
    }
    return tempData;
  }, [
    appointmentsData,
    appointmentsOnly,
    chats?.length,
    chatsData,
    customerData,
    guests?.length,
    guestsData,
    isChatSearchEnabled,
    isGuestSearchEnabled,
    type,
  ]);

  return (
    <SearchModal
      open={isOpen}
      toggleOpen={toggleOpen}
      onChange={searchBox.refine}
      placeholder={placeholder ?? 'Search for appointments or customers'}
      data={data}
      isLoading={isLoading}
      filter={
        appointmentsOnly && type !== 'chat'
          ? {
              options: (
                ['ALL', 'REQUESTED', 'ACCEPTED', 'SCHEDULED', 'CONFIRMED'] as (
                  | AppointmentStatus
                  | 'ALL'
                )[]
              ).map(s => ({
                label: getAppointmentStatusText(s),
                value: s,
              })),
              selected: appointmentStatus || 'ALL',
              onChange: newStatus =>
                update({
                  appointmentStatus: (newStatus === 'ALL'
                    ? null
                    : newStatus) as AppointmentStatus | null,
                }),
              placeholder: 'Select status',
              defaultValues: ['ALL'],
            }
          : undefined
      }
    />
  );
}

function CustomerAppointmentSearch() {
  const { toggle, isOpen } = useSearchModal();
  const { user } = useUser();
  const [isCustomerFormOpen, setIsCustomerFormOpen] = React.useState(false);

  useSearchShortcut(toggle, !user?.business);

  if (isCustomerFormOpen) {
    return (
      <FullPageModal
        modalOpen
        setModalOpen={setIsCustomerFormOpen}
        name="Add Customer">
        <CustomerForm
          setModalOpen={setIsCustomerFormOpen}
          openChatOnSave
          submitText="Create Chat"
          onCustomerSave={async () => {
            await searchClient.clearCache();
          }}
        />
      </FullPageModal>
    );
  }

  if (!isOpen || !user?.business) return null;

  return (
    <Wrapper>
      <CustomerAppointmentSearchComponent
        setIsCustomerFormOpen={setIsCustomerFormOpen}
      />
    </Wrapper>
  );
}

export default CustomerAppointmentSearch;
